import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCarriers, useTrunks } from '../../lib/api/hooks-billing.js';
import { StatusBadge, TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { api } from '../../lib/api/client.js';
import { TrunkEditor } from './TrunkEditor.js';
import { HealthBadge, RegistrationBadge } from './TrunkLive.js';
import type { Trunk } from '@arcnet/contracts';

/** How a trunk proves the calls are ours, in words. */
const AUTH_LABEL: Record<Trunk['auth_mode'], string> = {
  ip: 'IP address',
  digest: 'Digest',
  register: 'Digest + registration',
};

export function CarriersPage() {
  const can = useSession((s) => s.can);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | undefined>(undefined);
  /** The name being typed for a new carrier; null while the form is closed. */
  const [carrierName, setCarrierName] = useState<string | null>(null);
  const [addingTrunk, setAddingTrunk] = useState(false);
  const carriers = useCarriers();
  const trunks = useTrunks(selected);

  const createCarrier = useMutation({
    mutationFn: (name: string) => api.post<{ id: string }>('/v1/carriers', { name }),
    onSuccess: (created) => {
      setCarrierName(null);
      setSelected(created.id);
      void qc.invalidateQueries({ queryKey: ['carriers'] });
    },
  });
  const carrierList = carriers.data?.data ?? [];

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Carriers &amp; trunks</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Upstream SIP connectivity for inbound and outbound calls
          </p>
        </div>
      </div>

      {carriers.error && <ErrorBanner error={carriers.error} />}

      <div className="arc-grid" style={{ gridTemplateColumns: 'minmax(240px, 300px) 1fr', alignItems: 'start' }}>
        <div className="arc-card">
          <div className="arc-card-head">
            <h2 className="arc-h2">Carriers</h2>
            <div className="arc-spacer" />
            {can('carrier:write') && carrierName === null && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setCarrierName('')}>
                Add carrier
              </button>
            )}
          </div>
          {carrierName !== null && (
            <form className="arc-card-body" style={{ borderBottom: '1px solid var(--arc-border)' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (carrierName.trim()) createCarrier.mutate(carrierName.trim());
              }}>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="new-carrier">Carrier name</label>
                <input id="new-carrier" className="arc-input" autoFocus value={carrierName}
                  placeholder="Switch Tel" onChange={(e) => setCarrierName(e.target.value)} />
              </div>
              <div className="arc-row" style={{ gap: 6, marginTop: 10 }}>
                <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
                  disabled={!carrierName.trim() || createCarrier.isPending}>
                  {createCarrier.isPending ? 'Adding…' : 'Add'}
                </button>
                <button type="button" className="arc-btn arc-btn-sm" onClick={() => setCarrierName(null)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          <div>
            <button
              type="button"
              className="arc-nav-item"
              style={{ width: '100%', border: 'none', background: !selected ? 'var(--arc-accent-quiet)' : 'none', color: 'var(--arc-fg)', borderRadius: 0, padding: '10px 14px' }}
              onClick={() => setSelected(undefined)}
            >
              All carriers
            </button>
            {carrierList.map((c) => (
              <button
                key={c.id}
                type="button"
                className="arc-nav-item"
                style={{ width: '100%', border: 'none', background: selected === c.id ? 'var(--arc-accent-quiet)' : 'none', color: 'var(--arc-fg)', borderRadius: 0, padding: '10px 14px' }}
                onClick={() => setSelected(c.id)}
              >
                <div className="arc-stack" style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 550 }}>{c.name}</span>
                  <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                    {c.trunk_count ?? 0} trunk{c.trunk_count === 1 ? '' : 's'}
                  </span>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
            {carriers.data !== undefined && carrierList.length === 0 && (
              <Empty icon="carrier" title="No carriers" hint="Add a carrier to configure trunks." />
            )}
          </div>
        </div>

        <div>
          {addingTrunk && (
            <TrunkEditor carriers={carrierList} carrierId={selected}
              onDone={() => setAddingTrunk(false)} />
          )}

          <div className="arc-card">
            <div className="arc-card-head">
              <h2 className="arc-h2">Trunks</h2>
              <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
                {trunks.data ? `${trunks.data.data.length}` : ''}
              </span>
              <div className="arc-spacer" />
              {can('trunk:write') && !addingTrunk && carrierList.length > 0 && (
                <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
                  onClick={() => setAddingTrunk(true)}>
                  Add trunk
                </button>
              )}
            </div>

            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Host</th><th>Direction</th><th>Auth</th>
                    <th>Status</th><th>Registration</th>
                    <th className="arc-td-right">Capacity</th><th />
                  </tr>
                </thead>
                {trunks.data === undefined ? <TableSkeleton cols={8} /> : (
                  <tbody>
                    {trunks.data?.data.map((t) => (
                      <TrunkRow key={t.id} trunk={t} canTest={can('trunk:read')} canEdit={can('trunk:write')} />
                    ))}
                  </tbody>
                )}
              </table>
            </div>

            {trunks.data !== undefined && trunks.data?.data.length === 0 && (
              <Empty
                icon="carrier"
                title="No trunks configured"
                hint="Without a trunk there is no route to the PSTN — on-net calls work, external calls do not."
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function TrunkRow({ trunk, canTest, canEdit }: { trunk: Trunk; canTest: boolean; canEdit: boolean }) {
  const [testing, setTesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [results, setResults] = useState<Array<{ input: string; output: string; note: string | null }> | null>(null);

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post<{ results: Array<{ input: string; output: string; note: string | null }> }>(
        `/v1/trunks/${trunk.id}/actions/test-number-format`,
        { numbers: ['+27821234567', '+27115550100', '+441632960961'], direction: 'outbound' },
      );
      setResults(r.results);
    } finally { setTesting(false); }
  };

  return (
    <>
      <tr>
        <td>
          <div className="arc-stack">
            <span style={{ fontWeight: 550 }}>{trunk.name}</span>
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>{trunk.carrier_name}</span>
            {trunk.status !== 'active' && <span><StatusBadge status={trunk.status} /></span>}
          </div>
        </td>
        <td className="arc-mono arc-subtle">{trunk.host}:{trunk.port}/{trunk.transport}</td>
        <td><Badge tone="muted">{trunk.direction}</Badge></td>
        <td>
          <div className="arc-stack" style={{ fontSize: 12.5 }}>
            <span>{AUTH_LABEL[trunk.auth_mode]}</span>
            <span className="arc-subtle">
              {trunk.auth_mode === 'ip'
                ? `${trunk.source_ips.length} source${trunk.source_ips.length === 1 ? '' : 's'}`
                : trunk.auth_secret_set ? `${trunk.auth_username ?? ''} · password set` : 'no password'}
            </span>
          </div>
        </td>
        {/* As the switch measured it: an OPTIONS ping from our SIP address, and
            OpenSIPS's own record of the registration (TrunkLive.tsx). */}
        <td><HealthBadge live={trunk.live} /></td>
        <td><RegistrationBadge live={trunk.live} authMode={trunk.auth_mode} /></td>
        <td className="arc-td-right arc-num">
          {trunk.max_concurrent_calls ?? '∞'}
          <span className="arc-subtle"> calls</span>
        </td>
        <td className="arc-td-right">
          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
            {canTest && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={runTest} disabled={testing}>
                {testing ? '…' : 'Test format'}
              </button>
            )}
            {canEdit && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setEditing((v) => !v)}>
                {editing ? 'Close' : 'Configure'}
              </button>
            )}
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          {/* Full width: the editor is several groups of settings and squeezing
              it into a column would be worse than the dialog it replaces. */}
          <td colSpan={8} style={{ padding: 0 }}>
            <TrunkEditor trunk={trunk} onDone={() => setEditing(false)} />
          </td>
        </tr>
      )}
      {results && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--arc-surface-2)' }}>
            <div style={{ padding: '4px 0' }}>
              <div className="arc-subtle" style={{ fontSize: 12, marginBottom: 6 }}>
                Outbound transformation — what this carrier will receive:
              </div>
              {results.map((r) => (
                <div key={r.input} className="arc-mono" style={{ fontSize: 12.5, marginBottom: 2 }}>
                  {r.input} <span className="arc-subtle">→</span> <strong>{r.output}</strong>
                  {r.note && <span className="arc-subtle"> ({r.note})</span>}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
